import React from 'react';

/**
 * Locked action-icon color standard (bd-deepcontext-zp41). Same action kind
 * wears the same color everywhere a row-action icon appears — Brand
 * Blueprints, Scheduled Jobs, Site List modal, and anywhere else. Icon color
 * is fixed at rest; hover only adds a tinted background circle of the same
 * color family, it never changes the icon color. Supersedes the accent-hover
 * treatment from deepcontext-v84x.
 *
 * - panel   (funnel / simulator / view-runs): text-accent, hover:bg-nav-active
 * - edit    (pencil): text-warn, hover:bg-warn-bg
 * - delete  (trash): text-risk, hover:bg-risk-bg
 * - run     (play): text-safe, hover:bg-safe-bg
 * - active-toggle (power): semantic safe/risk by `active`, matching hover bg
 *
 * `active` also drives the open-panel state for `kind="panel"` — it stays on
 * bg-nav-active + text-accent-active while the panel is open, same as before.
 *
 * Ghost-hover kinds (deepcontext-4n4m.2): row/dismiss actions migrated off
 * local ICON_BUTTON/ACTION_ICON constants (UserManagement, SiteLists,
 * SiteListModal) whose hover was always the neutral ghost-bg tint rather
 * than a color-matched one — the icon color varies, the hover doesn't.
 *
 * - ghost        (edit / dismiss / back): text-ink-muted, hover:bg-ghost-bg
 * - ghost-danger (delete): text-risk, hover:bg-ghost-bg
 * - ghost-safe   (generate password link): text-safe, hover:bg-ghost-bg
 */
export type ActionIconKind =
    | 'panel'
    | 'edit'
    | 'delete'
    | 'run'
    | 'active-toggle'
    | 'ghost'
    | 'ghost-danger'
    | 'ghost-safe';

interface ActionIconButtonProps extends Omit<React.ButtonHTMLAttributes<HTMLButtonElement>, 'type'> {
    kind: ActionIconKind;
    /** Open state for `panel`; on/off state for `active-toggle`. Ignored otherwise. */
    active?: boolean;
}

const BASE =
    'inline-flex h-7 w-7 items-center justify-center rounded-full transition-colors focus:outline-none focus:ring-2 focus:ring-accent';

function colorClasses(kind: ActionIconKind, active: boolean | undefined): string {
    switch (kind) {
        case 'panel':
            return active ? 'bg-nav-active text-accent-active' : 'text-accent hover:bg-nav-active';
        case 'edit':
            return 'text-warn hover:bg-warn-bg';
        case 'delete':
            return 'text-risk hover:bg-risk-bg';
        case 'run':
            return 'text-safe hover:bg-safe-bg';
        case 'active-toggle':
            return active ? 'text-safe hover:bg-safe-bg' : 'text-risk hover:bg-risk-bg';
        case 'ghost':
            return 'text-ink-muted hover:bg-ghost-bg';
        case 'ghost-danger':
            return 'text-risk hover:bg-ghost-bg';
        case 'ghost-safe':
            return 'text-safe hover:bg-ghost-bg';
    }
}

export const ActionIconButton: React.FC<ActionIconButtonProps> = ({
    kind,
    active,
    className = '',
    children,
    ...props
}) => (
    <button type="button" className={`${BASE} ${colorClasses(kind, active)} ${className}`.trim()} {...props}>
        {children}
    </button>
);

export default ActionIconButton;
